import type { UploadErrorCodes } from "@eu.mdotm.api/sphere";

interface IUploadError {
	translationKey: string;
	type: "error" | "warning";
	referred: ("portfolio" | "universe" | "portfolioReference")[];
	isAliasReferred: boolean;
}

type IUploadErrorMap = {
	[errorKey in UploadErrorCodes]: IUploadError;
};

export const uploadErrorMap: IUploadErrorMap = {
	// User uploaded an invalid/parsable excel
	INVALID_WORKBOOK: {
		translationKey: "INVALID_WORKBOOK",
		type: "error",
		referred: [],
		isAliasReferred: false,
	},
	// The alias is not an isin or cusip
	INVALID_ALIAS: {
		translationKey: "INVALID_ALIAS",
		type: "error",
		referred: [],
		isAliasReferred: true,
	},
	// The weight entered is not in a valid format. Not present in the verify universe
	// INVALID_WEIGHT: { /* deprecated */
	// 	translationKey: "INVALID_WEIGHT",
	// 	type: "error",
	// 	referred: [],
	// 	isAliasReferred: true,
	// },
	// The sum of the weights is greater than 100. The sum of the weights is included in the payload. Not present in the verify universe
	WEIGHT_OVER_100: {
		/* deprecated */ translationKey: "WEIGHT_OVER_100",
		type: "error",
		referred: [],
		isAliasReferred: false,
	},
	// The sum of the weights is less than 100. The sum of the weights is included in the payload. Not present in the verify universe
	WEIGHT_UNDER_100: {
		/* deprecated */ translationKey: "WEIGHT_UNDER_100",
		type: "warning",
		referred: [],
		isAliasReferred: false,
	},
	// Include in the weight field the total number of tools loaded by the user.
	MORE_THAN_MAX_INSTRUMENTS: {
		translationKey: "MORE_THAN_MAX_INSTRUMENTS",
		type: "error",
		referred: [],
		isAliasReferred: false,
	},
	// A file filling a composition in quantities gives a quantity to an instrument held only by weight (cash, an index, a nested portfolio, a live proxy)
	QUANTITY_ON_WEIGHT_ONLY_INSTRUMENT: {
		translationKey: "QUANTITY_ON_WEIGHT_ONLY_INSTRUMENT",
		type: "error",
		referred: [],
		isAliasReferred: true,
	},
	// A file filling a composition in quantities gives both a weight and a quantity on the same row
	WEIGHT_AND_QUANTITY_ON_SAME_ROW: {
		translationKey: "WEIGHT_AND_QUANTITY_ON_SAME_ROW",
		type: "error",
		referred: [],
		isAliasReferred: true,
	},
	// In this phase, it indicates that the user has loaded two rows with the same alias (although perhaps with different weights). An entry is generated for each duplicate row.
	DUPLICATE_ROW: {
		translationKey: "DUPLICATE_ROW",
		type: "warning",
		referred: [],
		isAliasReferred: true,
	},
	// Can only happen in single stock portfolios. It means that the tool cannot be managed by sphere because it is not present on the factset or because the factset data is insufficient. The notified ticker does not appear inside the uploadComposition
	MISSING_DATA: {
		/* deprecated */ translationKey: "MISSING_DATA",
		type: "warning",
		referred: [],
		isAliasReferred: true,
	},
	// It can only happen in single stocks portfolios, in case the user enters an instrument that can be managed by sphere but which is not a stock
	// INCOMPATIBLE_WITH_TYPE: { /* deprecated */
	// 	translationKey: "INCOMPATIBLE_WITH_TYPE",
	// 	type: "warning",
	// 	referred: [],
	// 	isAliasReferred: true,
	// },
	// If all the tools are removed due to one of the two previous errors, this case occurs again.
	EMPTY_SS_PORTFOLIO: {
		/* deprecated */ translationKey: "EMPTY_SS_PORTFOLIO",
		type: "error",
		referred: [],
		isAliasReferred: true,
	},
	// Please note that instruments weighing less than 0.01 have been assigned to cash.
	// WEIGHT_TOO_SMALL: { /* deprecated */
	// 	translationKey: "WEIGHT_TOO_SMALL",
	// 	type: "warning",
	// 	referred: [],
	// 	isAliasReferred: true,
	// },
	// Please note that instruments weighing less than 0.01 have been assigned to cash.
	DELISTED: {
		translationKey: "DELISTED",
		type: "error",
		referred: [],
		isAliasReferred: true,
	},
};

// The backend can send a code the generated client does not list yet: it blocks the upload on its row.
export function uploadErrorFor(code: UploadErrorCodes | undefined): IUploadError {
	if (code === undefined || !Object.hasOwn(uploadErrorMap, code)) {
		return { translationKey: code ?? "INVALID_WORKBOOK", type: "error", referred: [], isAliasReferred: true };
	}
	return uploadErrorMap[code];
}
